import React, { useState, useEffect, useCallback } from 'react';
import { Mail, RefreshCw, Search, Filter, ChevronLeft, ChevronRight, CheckCircle, AlertCircle, Clock, Ban, BarChart3 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { adminFetch } from '../lib/api';

interface SendLogEntry {
  id: string;
  template_slug: string;
  mail_class: string;
  recipient_email: string;
  recipient_user_id: string | null;
  trigger_event: string | null;
  status: string;
  block_reason: string | null;
  variables_snapshot: Record<string, any>;
  resend_message_id: string | null;
  sent_at: string;
}

interface SendLogStats {
  total: number;
  last_24h: number;
  by_status: Record<string, number>;
  by_class: Record<string, number>;
  top_templates: { slug: string; count: number }[];
}

const STATUS_COLORS: Record<string, { bg: string; color: string; icon: React.ReactNode }> = {
  sent: { bg: 'rgba(16,185,129,0.1)', color: '#059669', icon: <CheckCircle size={12} /> },
  failed: { bg: 'rgba(239,68,68,0.1)', color: '#dc2626', icon: <AlertCircle size={12} /> },
  blocked_opt_in: { bg: 'rgba(245,158,11,0.1)', color: '#d97706', icon: <Ban size={12} /> },
  blocked_preference: { bg: 'rgba(245,158,11,0.1)', color: '#d97706', icon: <Ban size={12} /> },
  blocked_cooldown: { bg: 'rgba(168,85,247,0.1)', color: '#7c3aed', icon: <Clock size={12} /> },
  blocked_validation: { bg: 'rgba(239,68,68,0.1)', color: '#dc2626', icon: <AlertCircle size={12} /> },
};

const CLASS_COLORS: Record<string, string> = {
  security: '#dc2626',
  transactional: '#2563eb',
  service: '#6b7280',
  subscription: '#7c3aed',
  marketing: '#d97706',
  support: '#0d9488',
};

export const SendLogPage: React.FC = () => {
  const [logs, setLogs] = useState<SendLogEntry[]>([]);
  const [stats, setStats] = useState<SendLogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [slugFilter, setSlugFilter] = useState('');
  const [showStats, setShowStats] = useState(true);
  const limit = 25;

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (slugFilter) params.set('slug', slugFilter);
      const resp = await adminFetch(`/api/email/admin/send-logs?${params}`, {
              });
      const data = await resp.json();
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch {
      setLogs([]);
    }
    setLoading(false);
  }, [offset, slugFilter]);

  const loadStats = async () => {
    try {
      const resp = await adminFetch(`/api/email/admin/send-log-stats`, {
              });
      const data = await resp.json();
      setStats(data);
    } catch {
      setStats(null);
    }
  };

  useEffect(() => { loadLogs(); }, [loadLogs]);
  useEffect(() => { loadStats(); }, []);

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 style={{ margin: 0 }}><Mail size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />Send-Log</h2>
          <p className="text-sm text-muted" style={{ margin: '4px 0 0' }}>ETOS E-Mail-Versand-Protokoll</p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-ghost btn-sm" onClick={() => setShowStats(!showStats)}>
            <BarChart3 size={14} /> {showStats ? 'Stats ausblenden' : 'Stats anzeigen'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => { loadLogs(); loadStats(); }}>
            <RefreshCw size={14} /> Aktualisieren
          </button>
        </div>
      </div>

      {/* Stats Dashboard */}
      {showStats && stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          <div className="card" style={{ padding: 16, textAlign: 'center' }}>
            <div className="text-xs text-muted">Gesamt</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--primary)' }}>{stats.total}</div>
          </div>
          <div className="card" style={{ padding: 16, textAlign: 'center' }}>
            <div className="text-xs text-muted">Letzte 24h</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#059669' }}>{stats.last_24h}</div>
          </div>
          <div className="card" style={{ padding: 16 }}>
            <div className="text-xs text-muted" style={{ marginBottom: 8 }}>Status (24h)</div>
            {Object.entries(stats.by_status).map(([status, count]) => (
              <div key={status} className="flex justify-between" style={{ fontSize: 12, marginBottom: 2 }}>
                <span style={{ color: STATUS_COLORS[status]?.color || '#666' }}>{status}</span>
                <strong>{count}</strong>
              </div>
            ))}
            {Object.keys(stats.by_status).length === 0 && <div className="text-xs text-muted">Keine Daten</div>}
          </div>
          <div className="card" style={{ padding: 16 }}>
            <div className="text-xs text-muted" style={{ marginBottom: 8 }}>Top Templates (24h)</div>
            {stats.top_templates.slice(0, 5).map(t => (
              <div key={t.slug} className="flex justify-between" style={{ fontSize: 12, marginBottom: 2 }}>
                <code style={{ fontSize: 11 }}>{t.slug}</code>
                <strong>{t.count}</strong>
              </div>
            ))}
            {stats.top_templates.length === 0 && <div className="text-xs text-muted">Keine Daten</div>}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: 16 }}>
        <div className="flex gap-2 items-center">
          <Search size={14} className="text-muted" />
          <input
            className="input"
            value={slugFilter}
            onChange={e => { setSlugFilter(e.target.value); setOffset(0); }}
            placeholder="Nach Template-Slug filtern..."
            style={{ flex: 1, fontSize: 13 }}
          />
          {slugFilter && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setSlugFilter(''); setOffset(0); }}>
              Zurücksetzen
            </button>
          )}
          <span className="text-xs text-muted">{total} Einträge</span>
        </div>
      </div>

      {/* Log Table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'rgba(0,0,0,0.03)', borderBottom: '1px solid var(--border)' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>Zeitpunkt</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>Template</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>Klasse</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>Empfänger</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>Status</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>Details</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center' }}>
                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Laden...
              </td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center' }} className="text-muted">
                Keine Send-Log-Einträge gefunden
              </td></tr>
            ) : (
              logs.map(log => {
                const statusStyle = STATUS_COLORS[log.status] || { bg: '#f5f5f5', color: '#666', icon: null };
                return (
                  <tr key={log.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 14px', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {new Date(log.sent_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <code style={{ fontSize: 12, background: 'rgba(0,0,0,0.04)', padding: '2px 6px', borderRadius: 4 }}>{log.template_slug}</code>
                      {log.trigger_event && <div className="text-xs text-muted" style={{ marginTop: 2 }}>{log.trigger_event}</div>}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                        background: `${CLASS_COLORS[log.mail_class] || '#666'}15`,
                        color: CLASS_COLORS[log.mail_class] || '#666',
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                      }}>{log.mail_class}</span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12 }}>
                      {log.recipient_email}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                        background: statusStyle.bg, color: statusStyle.color,
                      }}>
                        {statusStyle.icon} {log.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 11, color: '#999', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.block_reason || (log.resend_message_id ? `ID: ${log.resend_message_id.substring(0, 12)}...` : '—')}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="text-xs text-muted">Seite {currentPage} von {totalPages}</span>
            <div className="flex gap-1">
              <button className="btn btn-ghost btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
                <ChevronLeft size={14} /> Zurück
              </button>
              <button className="btn btn-ghost btn-sm" disabled={currentPage >= totalPages} onClick={() => setOffset(offset + limit)}>
                Vor <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
